/**
 * PageNameLabel — the page's NAME as a Figma-style label at the node's top-left,
 * OUTSIDE the frame (web render). This is the canvas/builder identity of the
 * page (like a Figma frame name), distinct from the site ADDRESS shown inside
 * the device titlebar. Editing here edits the name (the slug derives from it);
 * a future ⋯ "Change URL…" overrides the slug.
 *
 * • Counter-scaled against the canvas zoom (`scale`) so it stays a constant
 *   on-screen size — never shrinks into the dots when you zoom out.
 * • A drag handle (`.wb-page-drag`) + it sits inside the node root, so clicking
 *   selects the page and dragging moves it (Figma muscle-memory).
 * • The drag class goes on through a ref (html.* takes no className; see
 *   usePageDragHandle). PageNode supplies a relative, non-clipping parent so
 *   the label can sit above the frame.
 * • A pencil affordance sits at the right, next to the name, on the SELECTED
 *   page. Unlike a hover-only affordance (invisible on touch) or double-click
 *   (double-TAP = pinch-zoom on a canvas), a tap target that appears on
 *   selection reads + works the same on desktop and touch — selecting is the
 *   natural precursor to editing — and it's the same edit primitive the native
 *   node uses. Double-click on the name stays as a desktop accelerator.
 */
import { css, html } from "react-strict-dom";
import { Input, Tooltip } from "@duro-app/ui";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import {
  microSpacing,
  radii,
  spacing,
} from "@duro-app/tokens/tokens/spacing.css";
import { typeScale, typography } from "@duro-app/tokens/tokens/typography.css";

import { usePageDragHandle } from "./PageTitleBar.js";
import { EditorButton } from "./ui/EditorButton.js";

interface PageNameLabelProps {
  label: string;
  readOnly: boolean;
  selected: boolean;
  /** True while the node is being dragged — hide the pencil (see below). */
  dragging: boolean;
  scale: number;
  editingLabel: boolean;
  draftLabel: string;
  setDraftLabel: (s: string) => void;
  startRename: () => void;
  commitRename: () => void;
  cancelRename: () => void;
}

const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

const s = css.create({
  label: {
    position: "absolute",
    left: 0,
    bottom: `calc(100% + ${microSpacing.px5})`,
    transformOrigin: "left bottom",
    display: "inline-flex",
    flexDirection: "row",
    alignItems: "center",
    gap: microSpacing.px6,
    maxWidth: sizes.popoverW,
    height: sizes.chip,
    paddingLeft: spacing.sm,
    paddingRight: spacing.sm,
    borderRadius: radii.sm,
    backgroundColor: colors.bgCard,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    boxShadow: shadows.sm,
    fontSize: typeScale.fontSize2,
    fontWeight: typography.fontWeightSemibold,
    color: colors.text,
    whiteSpace: "nowrap",
    cursor: "grab",
    userSelect: "none",
  },
  scaled: (scale: number) => ({
    transform: `scale(${scale})`,
  }),
  selected: {
    backgroundColor: colors.infoBg,
    borderColor: colors.accent,
  },
  readOnly: {
    cursor: "default",
  },
  grip: {
    color: colors.textMuted,
    fontSize: typography.fontSizeXs,
  },
  publishedDot: {
    display: "block",
    width: sizes.indicatorDot,
    height: sizes.indicatorDot,
    borderRadius: radii.full,
    backgroundColor: colors.success,
    flexShrink: 0,
  },
  inputWrap: {
    display: "block",
    width: "12ch",
    minWidth: 0,
  },
  name: {
    display: "block",
    // The name's share of the label's popoverW max: it ellipsizes inside the
    // Tooltip trigger, which (unlike a flex item) doesn't shrink on its own.
    maxWidth: sizes.popoverWSm,
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: colors.text,
  },
  nameEditable: {
    cursor: "text",
  },
});

export function PageNameLabel({
  label,
  readOnly,
  selected,
  dragging,
  scale,
  editingLabel,
  draftLabel,
  setDraftLabel,
  startRename,
  commitRename,
  cancelRename,
}: PageNameLabelProps) {
  const dragHandleRef = usePageDragHandle<HTMLDivElement>(!readOnly);
  const name = (
    <html.span
      // Double-click to rename. RSD has no onDoubleClick; on web its click is
      // React's MouseEvent, whose `detail` (the click count, untyped by RSD)
      // is 2 on the second click of a double-click.
      onClick={
        readOnly
          ? undefined
          : (e: object) => {
              if ((e as { detail?: number }).detail === 2) startRename();
            }
      }
      style={[s.name, !readOnly && s.nameEditable]}
    >
      {label}
    </html.span>
  );
  return (
    <html.div
      ref={dragHandleRef}
      style={[
        s.label,
        s.scaled(scale),
        selected && s.selected,
        readOnly && s.readOnly,
      ]}
    >
      {!readOnly && (
        <html.span aria-hidden style={s.grip}>
          ⠿
        </html.span>
      )}
      <Tooltip.Root content="Published">
        <Tooltip.Trigger>
          <html.span aria-hidden style={s.publishedDot} />
        </Tooltip.Trigger>
      </Tooltip.Root>
      {editingLabel ? (
        <html.span onPointerDown={stop} style={s.inputWrap}>
          <Input
            autoFocus
            aria-label="Page name"
            value={draftLabel}
            onChange={(e) => setDraftLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitRename();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancelRename();
              }
            }}
            onBlur={commitRename}
          />
        </html.span>
      ) : readOnly ? (
        name
      ) : (
        <Tooltip.Root content="Double-click to rename">
          <Tooltip.Trigger>{name}</Tooltip.Trigger>
        </Tooltip.Root>
      )}
      {/* Pencil — the primary, cross-platform edit affordance (see header).
          Shown only on the SELECTED page (contextual, not always-on clutter);
          hidden while editing (the input replaces it), in read-only, and DURING
          a drag: a drag is a spatial task, an edit target on a moving object is
          unhittable (Fitts) and invites mis-taps on touch — it returns the
          instant the node comes to rest (tied to xyflow's `dragging`). */}
      {!readOnly && !editingLabel && selected && !dragging && (
        <EditorButton
          variant="link"
          aria-label="Edit page name"
          title="Edit name"
          // Don't let the press start an xyflow node drag / selection.
          isolatePress
          onClick={startRename}
        >
          {/* holds-until: Duro Icon has an edit glyph (https://github.com/fredericrous/duro-design-system/issues/107) */}
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
          </svg>
        </EditorButton>
      )}
    </html.div>
  );
}
